/**
 * Commentary on the games SSE is accumulated on the server; each message can carry the full innings.
 * Client-side "append only new ball keys" breaks ordering when the first packets were just the latest over
 * and a later packet carries balls 1..N (we would append early overs after late ones).
 */

export type CommentaryBallRow = {
  ball?: number | string;
  commentary?: string;
  _corrected?: boolean;
  _correctedFrom?: string;
};

export type CommentaryPayload = { commentaries?: Record<string, CommentaryBallRow[]> };

/** Mirrors squaresBallResults.isIgnoreBallResult for scoregrid extra slots (wides / no-balls). */
function isScoregridIgnoreSlot(raw: unknown): boolean {
  if (raw == null || String(raw).trim() === "") return true;
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (lower.includes("pen")) return true;
  if (lower.includes("wide") || /(^|[^a-z])wd([^a-z]|$)/.test(lower)) return true;
  if (/^[1-7]w$/i.test(lower)) return true;
  if (/^w\+[1-5]w$/i.test(lower)) return true;
  if (/^[1-8]nb$/i.test(lower)) return true;
  if (/no\s*ball|(^|[^a-z])nb([^a-z]|$)/.test(lower)) return true;
  return false;
}

function filterToCountedBallResults(rawArray: unknown[]): string[] {
  const out: string[] = [];
  for (const v of rawArray) {
    if (v == null || String(v).trim() === "") continue;
    if (isScoregridIgnoreSlot(v)) continue;
    out.push(String(v).trim());
    if (out.length >= 6) break;
  }
  return out;
}

function parseCommentaryBall(ball: number | string | undefined | null): { over0: number; delivery1: number } | null {
  const n = typeof ball === "number" ? ball : parseFloat(String(ball ?? ""));
  if (!Number.isFinite(n)) return null;
  const over0 = Math.floor(n);
  const parts = n.toFixed(1).split(".");
  const delivery1 = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  if (!Number.isFinite(delivery1) || delivery1 < 1) return null;
  return { over0, delivery1 };
}

function commentaryBallId(over0: number, delivery1: number): number {
  return over0 + delivery1 / 10;
}

function commentaryResultPart(row: CommentaryBallRow): string {
  const text = String(row.commentary ?? "").trim();
  return text.includes(",") ? text.slice(text.indexOf(",") + 1).trim() : text;
}

function isNonOutcomeCommentaryResult(resultText: string): boolean {
  const s = String(resultText || "").trim().toLowerCase();
  if (!s) return false;
  return /umpire|review|penalty|\bpen\b|wide|\bwd\b|no\s*ball|\bnb\b|dead\s*ball|lost\s*ball|retired|timed\s*out/.test(s);
}

function isExtraCommentaryRow(row: CommentaryBallRow): boolean {
  if (isNonOutcomeCommentaryResult(commentaryResultPart(row))) return true;
  const text = String(row.commentary ?? "").trim().toLowerCase();
  return /\bwide\b|\bno\s*ball\b|\bnb\b|\bwd\b/.test(text);
}

/**
 * Canonical delivery key. Decimal may use fine-grained ids (e.g. 0.61) while scoregrid synthesis
 * uses coarse ids (0.6); the UI shows one decimal place so both must merge to one row.
 */
export function commentaryBallMergeKey(ball: number | string | undefined | null): string {
  const parsed = parseCommentaryBall(ball);
  if (parsed) return commentaryBallId(parsed.over0, parsed.delivery1).toString();
  const n = typeof ball === "number" ? ball : parseFloat(String(ball ?? ""));
  return Number.isFinite(n) ? n.toString() : "";
}

function normalizeCommentaryBall(ball: number | string | undefined): number | string | undefined {
  const parsed = parseCommentaryBall(ball);
  if (!parsed) return ball;
  return commentaryBallId(parsed.over0, parsed.delivery1);
}

function isCoarseCommentaryBall(ball: number | string | undefined): boolean {
  const n = typeof ball === "number" ? ball : parseFloat(String(ball ?? ""));
  if (!Number.isFinite(n)) return true;
  const parsed = parseCommentaryBall(ball);
  if (!parsed) return true;
  return Math.abs(n - commentaryBallId(parsed.over0, parsed.delivery1)) < 1e-9;
}

function canonicalCommentaryDisplayKey(ball: number | string | undefined): string {
  const parsed = parseCommentaryBall(ball);
  if (!parsed) return "";
  return `${parsed.over0}.${parsed.delivery1}`;
}

/** Fine-grained ball id, or null when the id is coarse / unparseable. */
function commentaryFineBallId(ball: number | string | undefined): number | null {
  if (isCoarseCommentaryBall(ball)) return null;
  const n = typeof ball === "number" ? ball : parseFloat(String(ball ?? ""));
  return Number.isFinite(n) ? n : null;
}

/**
 * Outcome-only identity of an extras row, stable across enrichment passes: the bowler/batter head
 * is rewritten as lineups arrive and Decimal may re-announce the same extra as a thin
 * "CORRECTION: wide + 1." note, so only the result text can identify the delivery.
 */
function commentaryExtraResultKey(row: CommentaryBallRow): string {
  const s = commentaryResultPart(row)
    .replace(/^correction\s*[:–—-]?\s*/i, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.\s]+$/, "")
    .trim();
  return s || "extra";
}

/**
 * Extras collapse per display ball and outcome text, so "wide + 1" and "wide + 4" at the same ball
 * stay separate while the same wide re-sent in packet after packet does not pile up. Keying extras
 * on the ball id instead is not an option: Decimal mostly sends coarse ids for extras and
 * commentary arrives as accumulated innings history, so an id-based key cannot tell a genuinely
 * second wide from the first wide arriving again.
 */
function commentaryDeliveryMergeKey(row: CommentaryBallRow): string | null {
  const displayKey = canonicalCommentaryDisplayKey(row.ball);
  if (!displayKey) return null;
  if (!isExtraCommentaryRow(row)) return `n:${displayKey}`;
  return `e:${displayKey}:${commentaryExtraResultKey(row)}`;
}

/**
 * Newest-first display order: at the same display ball, legal re-bowl above wide/no-ball
 * (the extra is bowled first; the counted delivery is the redo).
 */
export function compareCommentaryDisplayOrder(a: CommentaryBallRow, b: CommentaryBallRow): number {
  const ba = typeof a.ball === "number" ? a.ball : parseFloat(String(a.ball ?? ""));
  const bb = typeof b.ball === "number" ? b.ball : parseFloat(String(b.ball ?? ""));
  const parsedA = parseCommentaryBall(a.ball);
  const parsedB = parseCommentaryBall(b.ball);
  const canonA = parsedA ? commentaryBallId(parsedA.over0, parsedA.delivery1) : ba;
  const canonB = parsedB ? commentaryBallId(parsedB.over0, parsedB.delivery1) : bb;
  if (canonA !== canonB) return canonB - canonA;

  const extraA = isExtraCommentaryRow(a) ? 1 : 0;
  const extraB = isExtraCommentaryRow(b) ? 1 : 0;
  if (extraA !== extraB) return extraA - extraB;

  return bb - ba;
}

export function sortCommentaryForDisplay(arr: CommentaryBallRow[]): CommentaryBallRow[] {
  return [...arr].sort(compareCommentaryDisplayOrder);
}

export function sortCommentaryByBall(arr: CommentaryBallRow[]): CommentaryBallRow[] {
  return sortCommentaryForDisplay(arr);
}

function commentaryHasAnalysisClause(text: string): boolean {
  return /\s\([^)]{4,}\)\s*$/.test(String(text ?? "").trim());
}

function commentaryRowQualityScore(row: CommentaryBallRow): number {
  const text = String(row.commentary ?? "").trim();
  if (!text) return 0;
  const resultPart = text.includes(",") ? text.slice(text.indexOf(",") + 1).trim() : "";
  if (isNonOutcomeCommentaryResult(resultPart)) return 6;
  let base = 1;
  const head = text.split(",")[0]?.trim() ?? "";
  const m = head.match(/^(.+?)\s+to\s+(.+)$/i);
  if (m) {
    const a = m[1].trim().toLowerCase();
    const b = m[2].trim().toLowerCase();
    if (a && b && a === b) base = 1;
    else if (a && b && a !== b) base = 4;
  } else if (/^\s*to\s+/i.test(head)) {
    base = 3;
  } else if (!head.includes(" to ")) {
    base = 1;
  } else {
    base = text.length >= 12 ? 2 : 1;
  }
  if (commentaryHasAnalysisClause(text)) base += 3;
  return base;
}

function pickPreferredCommentaryRow(
  existing: CommentaryBallRow,
  incoming: CommentaryBallRow
): CommentaryBallRow {
  const se = commentaryRowQualityScore(existing);
  const si = commentaryRowQualityScore(incoming);
  let chosen = existing;
  if (si > se) chosen = incoming;
  else if (si < se) chosen = existing;
  else {
    const existingText = String(existing.commentary ?? "");
    const incomingText = String(incoming.commentary ?? "");
    chosen = incomingText.length > existingText.length ? incoming : existing;
  }
  return { ...chosen, ball: normalizeCommentaryBall(chosen.ball) };
}

function mergeCommentaryBallRows(rows: CommentaryBallRow[]): CommentaryBallRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const byKey = new Map<string, CommentaryBallRow>();
  const extraKeyByFineBall = new Map<number, string>();
  let uniqueSeq = 0;
  for (const item of rows) {
    const normalized: CommentaryBallRow = {
      ...item,
      ball: isCoarseCommentaryBall(item.ball) ? normalizeCommentaryBall(item.ball) : item.ball,
    };
    const isExtra = isExtraCommentaryRow(normalized);
    const fineBall = commentaryFineBallId(normalized.ball);
    // Decimal republishing a mistaken wide/no-ball as a legal outcome on the same fine id.
    if (!isExtra && fineBall != null) {
      const staleKey = extraKeyByFineBall.get(fineBall);
      if (staleKey != null) {
        byKey.delete(staleKey);
        extraKeyByFineBall.delete(fineBall);
      }
    }
    const key = commentaryDeliveryMergeKey(normalized) ?? `u:${uniqueSeq++}`;
    const existing = byKey.get(key);
    byKey.set(key, existing ? pickPreferredCommentaryRow(existing, normalized) : normalized);
    if (isExtra && fineBall != null) extraKeyByFineBall.set(fineBall, key);
  }
  return sortCommentaryForDisplay(Array.from(byKey.values()));
}

function commentaryListsEqual(a: CommentaryBallRow[], b: CommentaryBallRow[]): boolean {
  if (a.length !== b.length) return false;
  const as = sortCommentaryForDisplay([...a]);
  const bs = sortCommentaryForDisplay([...b]);
  for (let i = 0; i < as.length; i++) {
    if (as[i].ball !== bs[i].ball || as[i].commentary !== bs[i].commentary) return false;
  }
  return true;
}

function scoregridDeliverySlots(row: Record<string, unknown> | null | undefined): unknown[] {
  if (!row || typeof row !== "object") return [];
  return [row.b1, row.b2, row.b3, row.b4, row.b5, row.b6, row.b7, row.b8, row.b9, row.b10, row.b11, row.b12];
}

function getScoregridOverRow(
  scoregrid: unknown,
  innsKey: string,
  over1Based: number
): Record<string, unknown> | null {
  if (!scoregrid || typeof scoregrid !== "object") return null;
  const sg = scoregrid as { overs?: Record<string, Array<Record<string, unknown>>> };
  const rows = sg.overs?.[innsKey];
  if (!Array.isArray(rows)) return null;
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const ov = parseInt(String(row.ov ?? ""), 10);
    if (ov === over1Based) return row;
  }
  return null;
}

function countScoregridExtrasBeforeDelivery(
  overRow: Record<string, unknown> | null,
  delivery1: number
): number | null {
  if (!overRow || delivery1 < 1) return null;
  const slots = scoregridDeliverySlots(overRow);
  let hasAnySlot = false;
  let counted = 0;
  let extrasBefore = 0;
  for (const raw of slots) {
    if (raw == null || String(raw).trim() === "") continue;
    hasAnySlot = true;
    if (isScoregridIgnoreSlot(raw)) {
      if (counted === delivery1 - 1) extrasBefore += 1;
      continue;
    }
    counted += 1;
    if (counted >= delivery1) break;
  }
  if (!hasAnySlot) return null;
  return extrasBefore;
}

/**
 * Drop commentary extras that scoregrid no longer supports alongside a legal row at the same
 * display ball (e.g. mistaken "wide + 4" left behind after Decimal corrected to "Four").
 */
export function pruneOrphanExtraCommentaryRows(
  rows: CommentaryBallRow[],
  scoregrid: unknown,
  innsKey: string
): CommentaryBallRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  if (!scoregrid || typeof scoregrid !== "object") return rows;

  type Group = {
    extras: CommentaryBallRow[];
    legals: CommentaryBallRow[];
    over1: number;
    delivery1: number;
  };
  const byDisplay = new Map<string, Group>();
  for (const row of rows) {
    if (!row) continue;
    const parsed = parseCommentaryBall(row.ball);
    if (!parsed) continue;
    const displayKey = `${parsed.over0}.${parsed.delivery1}`;
    let group = byDisplay.get(displayKey);
    if (!group) {
      group = { extras: [], legals: [], over1: parsed.over0 + 1, delivery1: parsed.delivery1 };
      byDisplay.set(displayKey, group);
    }
    if (isExtraCommentaryRow(row)) group.extras.push(row);
    else group.legals.push(row);
  }

  const drop = new Set<CommentaryBallRow>();
  for (const group of byDisplay.values()) {
    if (group.extras.length === 0 || group.legals.length === 0) continue;
    const overRow = getScoregridOverRow(scoregrid, innsKey, group.over1);
    const allowed = countScoregridExtrasBeforeDelivery(overRow, group.delivery1);
    if (allowed == null) continue;
    const counted = filterToCountedBallResults(scoregridDeliverySlots(overRow));
    if (counted.length < group.delivery1) continue;
    if (allowed <= 0) {
      for (const e of group.extras) drop.add(e);
      continue;
    }
    if (group.extras.length > allowed) {
      const excess = group.extras.length - allowed;
      for (let i = 0; i < excess; i++) drop.add(group.extras[i]);
    }
  }
  if (drop.size === 0) return rows;
  return rows.filter((r) => !drop.has(r));
}

/**
 * Merge `incomingPayload` into previous commentary state.
 * Keeps wide/no-ball and the legal re-bowl at the same display ball as separate rows, but drops
 * an extra when Decimal reclassifies that same fine-grained ball id as a legal outcome.
 */
export function mergeStreamCommentaryPayload(
  prevPayload: CommentaryPayload | null | undefined,
  incomingPayload: CommentaryPayload | null | undefined
): CommentaryPayload | null | undefined {
  if (!incomingPayload?.commentaries || Object.keys(incomingPayload.commentaries).length === 0) {
    return prevPayload ?? undefined;
  }

  const prev = prevPayload || {};
  const incoming = incomingPayload.commentaries;
  const nextCommentaries: Record<string, CommentaryBallRow[]> = { ...(prev.commentaries || {}) };
  let changed = false;

  for (const inningsKey of Object.keys(incoming)) {
    const newInnings = incoming[inningsKey];
    if (!Array.isArray(newInnings) || newInnings.length === 0) continue;

    const prevArr = nextCommentaries[inningsKey] || [];
    const merged = mergeCommentaryBallRows([...prevArr, ...newInnings]);
    if (!commentaryListsEqual(prevArr, merged)) {
      nextCommentaries[inningsKey] = merged;
      changed = true;
    }
  }

  if (!changed) return prevPayload;
  return { ...prev, ...incomingPayload, commentaries: nextCommentaries };
}
